import { useCallback, useState } from 'react'
import { useMutation } from '@apollo/react-hooks'
import { useTranslation } from 'react-i18next'
import { toastr } from 'react-redux-toastr'
import * as Mutation from '../../../../api/gql/mutation'
import { AccessLevel } from '../lib/levelMeta'
import { ModuleKey } from '../lib/moduleMeta'

const BATCH_SIZE = 10

export interface SetLevelOp { userId: string; module: ModuleKey; level: AccessLevel }

export function useMemberActions(projectId: string, refetch: () => Promise<any>) {
  const { t } = useTranslation()
  const [setLevel] = useMutation(Mutation.SET_USER_MODULE_LEVEL)
  const [setActions] = useMutation(Mutation.SET_USER_MODULE_ACTIONS)
  const [clearLevel] = useMutation(Mutation.CLEAR_USER_MODULE_ACCESS)
  const [setUserProjectRole] = useMutation(Mutation.SET_USER_PROJECT_ROLE)
  const [addProjectUsers] = useMutation(Mutation.ADD_PROJECT_USERS)
  const [removeProjectUsers] = useMutation(Mutation.REMOVE_PROJECT_USERS)
  const [updateUser] = useMutation(Mutation.UPDATE_USER)
  const [running, setRunning] = useState(false)

  const setModuleLevel = useCallback(async (userId: string, module: ModuleKey, level: AccessLevel) => {
    setRunning(true)
    try {
      await setLevel({ variables: { userId, projectId, module, level } })
      await refetch()
    } catch (e: any) {
      toastr.error(t('error', 'Error'), e.message)
    } finally {
      setRunning(false)
    }
  }, [setLevel, projectId, refetch, t])

  const setModuleActions = useCallback(async (userId: string, module: ModuleKey, actions: string[]) => {
    setRunning(true)
    try {
      await setActions({ variables: { userId, projectId, module, actions } })
      await refetch()
    } catch (e: any) {
      toastr.error(t('error', 'Error'), e.message)
    } finally {
      setRunning(false)
    }
  }, [setActions, projectId, refetch, t])

  const resetModule = useCallback(async (userId: string, module: ModuleKey) => {
    setRunning(true)
    try {
      await clearLevel({ variables: { userId, projectId, module } })
      await refetch()
    } catch (e: any) {
      toastr.error(t('error', 'Error'), e.message)
    } finally {
      setRunning(false)
    }
  }, [clearLevel, projectId, refetch, t])

  const changeRole = useCallback(async (projectUserId: string, roleId: string) => {
    setRunning(true)
    try {
      await setUserProjectRole({ variables: { id: projectUserId, roleId } })
      await refetch()
    } catch (e: any) {
      toastr.error(t('error', 'Error'), e.message)
    } finally {
      setRunning(false)
    }
  }, [setUserProjectRole, refetch, t])

  const addMember = useCallback(async (userId: string, roleId: string) => {
    setRunning(true)
    try {
      await addProjectUsers({ variables: { id: projectId, users: [{ id: userId, roleId }] } })
      await refetch()
    } catch (e: any) {
      toastr.error(t('error', 'Error'), e.message)
    } finally {
      setRunning(false)
    }
  }, [addProjectUsers, projectId, refetch, t])

  const setCompany = useCallback(async (userId: string, company: string) => {
    setRunning(true)
    try {
      await updateUser({ variables: { id: userId, company } })
      await refetch()
    } catch (e: any) {
      toastr.error(t('error', 'Error'), e.message)
    } finally {
      setRunning(false)
    }
  }, [updateUser, refetch, t])

  const removeMember = useCallback(async (userId: string) => {
    setRunning(true)
    try {
      await removeProjectUsers({ variables: { id: projectId, users: [userId] } })
      await refetch()
    } catch (e: any) {
      toastr.error(t('error', 'Error'), e.message)
    } finally {
      setRunning(false)
    }
  }, [removeProjectUsers, projectId, refetch, t])

  const bulkApply = useCallback(async (
    userIds: string[], module: ModuleKey, level: AccessLevel | null,
  ) => {
    if (userIds.length === 0) return
    setRunning(true)
    let ok = 0, failed = 0
    for (let i = 0; i < userIds.length; i += BATCH_SIZE) {
      const batch = userIds.slice(i, i + BATCH_SIZE)
      const results = await Promise.allSettled(batch.map((userId) =>
        level === null
          ? clearLevel({ variables: { userId, projectId, module } })
          : setLevel({ variables: { userId, projectId, module, level } }),
      ))
      results.forEach((r) => (r.status === 'fulfilled' ? ok++ : failed++))
      if (userIds.length > BATCH_SIZE) {
        toastr.info('', t('Members_bulk_progress', { done: ok + failed, total: userIds.length }))
      }
    }
    setRunning(false)
    if (failed === 0) toastr.success('', t('Members_bulk_success', { count: ok }))
    else toastr.warning('', t('Members_bulk_partial', { ok, failed }))
    await refetch()
  }, [setLevel, clearLevel, projectId, refetch, t])

  return { running, setModuleLevel, setModuleActions, resetModule, changeRole, addMember, removeMember, bulkApply, setCompany }
}
